import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AssetBinaryImage, AssetBinaryVideo } from '@/components/domain/AssetBinaryPreview';
import { downloadAssetContentFile, getAsset } from '@/services/assets';
import { formatRelativeDate } from '@/utils/date';
import type { Asset } from '@/types/api';

/**
 * Asset detail page: single asset view (M3 layout and primitives).
 */
export default function AssetDetailPage() {
  const { assetId: assetIdParam } = useParams();
  const navigate = useNavigate();
  const assetId = assetIdParam != null ? parseInt(assetIdParam, 10) : NaN;

  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    if (Number.isNaN(assetId)) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError('');

    getAsset(assetId)
      .then(setAsset)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load asset'))
      .finally(() => setLoading(false));
  }, [assetId]);

  if (Number.isNaN(assetId)) {
    return (
      <div className="p-8 md:p-12">
        <p className="text-error font-bold">Invalid asset ID.</p>
        <Link
          to="/assets"
          className="inline-flex items-center gap-1 mt-4 text-primary font-bold hover:underline"
        >
          <span className="material-symbols-outlined text-sm">arrow_back</span>
          Back to Assets
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <span className="material-symbols-outlined animate-spin text-4xl text-primary">
          progress_activity
        </span>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="p-8 md:p-12 max-w-5xl mx-auto flex flex-col items-center justify-center min-h-[40vh]">
        <span className="material-symbols-outlined text-5xl text-error/30 mb-4">broken_image</span>
        <p className="text-on-surface-variant">{error || 'Asset not found.'}</p>
        <Link
          to="/assets"
          className="inline-flex items-center gap-1 mt-6 text-primary font-bold hover:underline"
        >
          <span className="material-symbols-outlined text-sm">arrow_back</span>
          Back to Assets
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto p-4 md:p-8 lg:p-12 space-y-8">
      {/* Back nav */}
      <button
        type="button"
        onClick={() => navigate('/assets')}
        className="flex items-center gap-1.5 text-on-surface-variant hover:text-primary transition-colors font-bold text-sm cursor-pointer"
      >
        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
        Assets
      </button>

      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-headline font-black text-on-surface truncate">{asset.name}</h1>
        <div className="flex flex-wrap items-center gap-3 text-sm text-on-surface-variant font-medium">
          <span className="px-2 py-0.5 rounded-full bg-surface-container-high text-on-surface border border-outline-variant/10 text-xs font-black uppercase tracking-wider">
            {asset.asset_type === 'video' ? 'Video' : 'Image'}
          </span>
          <span>Modified {formatRelativeDate(asset.updated_at)}</span>
        </div>
        {asset.alt_text && (
          <p className="text-sm text-on-surface-variant mt-2 border-l-2 border-primary/30 pl-3">
            {asset.alt_text}
          </p>
        )}
      </div>

      {/* Preview Container */}
      <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/10 ambient-shadow overflow-hidden flex flex-col min-h-[400px]">
        {/* Top bar */}
        <div className="px-4 py-3 bg-surface-container-low border-b border-outline-variant/10 flex justify-between items-center text-xs font-bold text-on-surface-variant">
          <span className="uppercase tracking-wider">Preview window</span>
          <button
            type="button"
            disabled={downloading || asset.id == null}
            onClick={() => {
              if (asset.id == null) return;
              setDownloadError('');
              setDownloading(true);
              void downloadAssetContentFile(asset.id, asset.name)
                .catch((err: unknown) =>
                  setDownloadError(err instanceof Error ? err.message : 'Download failed'),
                )
                .finally(() => setDownloading(false));
            }}
            className="flex items-center gap-1.5 text-primary hover:text-primary-dim transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[16px]">download</span>
            {downloading ? 'Downloading…' : 'Download Original'}
          </button>
        </div>
        {downloadError && (
          <div className="px-4 py-2 text-xs bg-error-container text-on-error-container">
            {downloadError}
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 p-8 bg-surface-container-low/20 flex flex-col items-center justify-center">
          <div className="max-w-full max-h-[65vh] rounded-lg overflow-hidden shadow-sm border border-outline-variant/5">
            {asset.id == null ? (
              <div className="w-[400px] h-[300px] max-w-full flex items-center justify-center bg-surface-container-low text-on-surface-variant">
                Asset preview unavailable
              </div>
            ) : asset.asset_type === 'image' ? (
              <AssetBinaryImage
                assetId={asset.id}
                endpoint="content"
                remoteUrlHint={asset.uri}
                alt={asset.alt_text || asset.name}
                className="w-auto h-auto max-w-full max-h-[65vh] object-contain"
                loadingClassName="w-[400px] h-[300px] max-w-full flex items-center justify-center bg-surface-container-low"
              />
            ) : (
              <AssetBinaryVideo
                assetId={asset.id}
                uriHint={asset.uri}
                thumbnailUrlHint={asset.thumbnail_url}
                className="w-auto h-auto max-w-full max-h-[65vh] object-contain"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
